import dayjs from "dayjs";
import { CalendarRange, ExternalLink } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

import { AssignedUsersAvatarStack } from "@/features/foreignNationals/components/AssignedUsersAvatarStack";
import { formatEventTime, type CalendarEvent } from "../calendarModel";

const PROVIDER_LABEL: Record<CalendarEvent["source"], string> = {
  reminder: "Reminder",
  google: "Google Calendar",
  outlook: "Outlook"
};

type Props = {
  /** The synced event to describe; null closes the popover. */
  event: CalendarEvent | null;
  onOpenChange: (open: boolean) => void;
  /** Resolves a teammate's display name; falls back to "a teammate". */
  nameOf?: (uid: string) => string | undefined;
  /** The element the popover is anchored to. */
  children: ReactNode;
};

/**
 * Read-only details for a synced (Google / Outlook) event: title, time,
 * whose calendar it came from, and a link to open it in the provider.
 * Synced events can only be changed in their source calendar.
 */
export const ExternalEventPopover: FC<Props> = ({
  event,
  onOpenChange,
  nameOf,
  children
}) => {
  const owner = event?.ownerUid;
  const ownerName = owner ? nameOf?.(owner) : undefined;
  const owners = event?.ownerUids ?? [];
  const shared = owners.length > 1;
  return (
    <Popover open={event !== null} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {event ? (
        <PopoverContent
          align="start"
          className="w-72 space-y-2 p-3"
          data-testid="external-event-popover"
        >
          <div className="flex items-start gap-2">
            <CalendarRange className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{event.title}</p>
              <p className="text-xs text-muted-foreground">
                {dayjs(event.start).format("ddd, MMM D")} ·{" "}
                {formatEventTime(event)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {shared ? (
              <AssignedUsersAvatarStack userIds={owners} nonInteractive />
            ) : owner ? (
              <AssignedUsersAvatarStack
                userIds={[owner]}
                maxVisible={1}
                nonInteractive
              />
            ) : null}
            <span className="truncate">
              {shared
                ? `On ${owners
                    .map((uid) => nameOf?.(uid) ?? "a teammate")
                    .join(" and ")}'s calendars`
                : `${ownerName ?? "A teammate"}'s ${
                    PROVIDER_LABEL[event.source]
                  }`}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Synced read-only. Edit it in {PROVIDER_LABEL[event.source]}.
          </p>
          {event.externalUrl ? (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="h-7 w-full gap-1.5 text-xs"
            >
              <a href={event.externalUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="size-3.5" />
                Open in {PROVIDER_LABEL[event.source]}
              </a>
            </Button>
          ) : null}
        </PopoverContent>
      ) : null}
    </Popover>
  );
};
